import { useContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageContext from '@/Providers/PageContextProvider/PageContext';

export const layoutModes = ['default', 'split', 'background'] as const;

export type LayoutMode = (typeof layoutModes)[number];

/** Query-param name that overrides the layout for a single link. */
export const LAYOUT_PARAM = 'layout';
/** SessionStorage key that persists the layout across the OIDC redirect. */
export const LAYOUT_STORAGE_KEY = 'logto:layout';

const layoutModeSet = new Set<string>(layoutModes);

const isLayoutMode = (value: unknown): value is LayoutMode =>
  typeof value === 'string' && layoutModeSet.has(value);

type ResolveInput = {
  paramValue?: string;
  storedValue?: string;
  configValue?: string;
};

/**
 * Resolve the active layout mode from ordered sources:
 * param > sessionStorage > branding.loginLayout > 'default'.
 * Unknown values at any source are skipped, not treated as 'default',
 * so a bad param still allows a valid stored/config value to win.
 */
export const resolveLayoutMode = ({
  paramValue,
  storedValue,
  configValue,
}: ResolveInput): LayoutMode => {
  for (const candidate of [paramValue, storedValue, configValue]) {
    if (isLayoutMode(candidate)) {
      return candidate;
    }
  }

  return 'default';
};

const readStorage = (key: string): string | undefined => {
  try {
    return sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable (privacy mode); resolution still works via param/config.
  }
};

/**
 * Resolve the active layout mode and persist a valid `?layout=` param so it
 * survives the OIDC redirect (which strips unknown query params).
 */
export const useLayoutMode = (): LayoutMode => {
  const [searchParams] = useSearchParams();
  const { experienceSettings } = useContext(PageContext);

  const paramValue = searchParams.get(LAYOUT_PARAM) ?? undefined;

  // Persist a valid param so it survives the OIDC redirect (which strips unknown query params).
  useEffect(() => {
    if (isLayoutMode(paramValue)) {
      writeStorage(LAYOUT_STORAGE_KEY, paramValue);
    }
  }, [paramValue]);

  const configValue = experienceSettings?.branding.loginLayout;

  return useMemo(
    () =>
      resolveLayoutMode({
        paramValue,
        storedValue: readStorage(LAYOUT_STORAGE_KEY),
        configValue,
      }),
    [paramValue, configValue]
  );
};
